/**
 * Selectores de fecha y hora: español + apertura al hacer clic en el campo
 * -----------------------------------------------------------------------------
 * Dos cosas que MDB no da de fábrica:
 *
 * 1. IDIOMA. Los pickers vienen en inglés y MDB no expone un `Default` estático
 *    mutable. Los meses y días son arrays, así que tampoco pueden pasarse por
 *    `data-mdb-*`: `normalizeData()` no parsea JSON y los dejaría como string.
 *    La única vía es construir la instancia con un objeto de opciones. Esto
 *    corre ANTES de `initMDB()`; como el autoinit de MDB hace `getInstance()`
 *    antes de crear, respeta lo ya instanciado y no lo duplica.
 *
 * 2. APERTURA POR CLIC. Por defecto el picker solo abre desde su iconito. Aquí
 *    se abre al hacer clic en cualquier parte del campo.
 *
 *    No se usa la opción `disableInput` de MDB para bloquear el tecleo: hace
 *    `input.disabled = true`, y un campo deshabilitado NO se envía con el
 *    formulario. Se marca `readonly`, que sí se envía.
 *
 *    Tampoco se llama a `instance.open()`: el Timepicker no tiene ese método
 *    público. Se reenvía el clic al botón toggle del propio MDB, que es el
 *    camino de apertura que los tres componentes comparten.
 *
 * Prioridad de configuración: `data-mdb-*` del elemento > español > default MDB.
 * Opt-outs: `data-mdb-no-locale` (idioma), `data-mdb-no-click-open` (apertura).
 */

import { datepickerES, timepickerES, datetimepickerES } from './locale-es.js';

const PICKERS = [
  {
    selector: '[data-mdb-datepicker-init]',
    component: 'Datepicker',
    toggle: '.datepicker-toggle-button',
    options: () => datepickerES,
  },
  {
    selector: '[data-mdb-timepicker-init]',
    component: 'Timepicker',
    toggle: '.timepicker-toggle-button',
    options: () => timepickerES,
  },
  {
    selector: '[data-mdb-datetimepicker-init]',
    component: 'Datetimepicker',
    toggle: '.datetimepicker-toggle-button',
    // El datetimepicker no recibe las claves sueltas: las reparte en dos
    // objetos anidados para su datepicker y su timepicker internos.
    //
    // **Y su fecha va en `dd/mm/yyyy`, no en ISO como la del datepicker
    // suelto.** No es una inconsistencia: es que los campos llevan cosas
    // distintas. Una fecha sola se pinta con el `Y-m-d` que viene de la base;
    // una fecha con hora se pinta ya formateada -«27/08/2026, 09:47 PM»- porque
    // es lo que produce y lo que vuelve a leer este componente.
    //
    // Se supo rompiéndolo: al pasar el datepicker a ISO, el de fecha y hora
    // heredó el formato y **marcaba «Formato de fecha u hora inválido» nada más
    // abrir la pantalla**, sobre un valor que estaba perfectamente bien.
    options: () => ({
      ...datetimepickerES,
      datepicker: { ...datepickerES, format: 'dd/mm/yyyy' },
      timepicker: timepickerES,
    }),
  },
];

/**
 * Quita del objeto de opciones las claves que el elemento ya define por
 * `data-mdb-*`, para que la configuración puntual del HTML gane sobre el idioma.
 */
function withElementOverrides(element, options) {
  const config = { ...options };

  Object.keys(config).forEach((key) => {
    const attr = `data-mdb-${key.replace(/[A-Z]/g, (chr) => `-${chr.toLowerCase()}`)}`;
    if (element.hasAttribute(attr)) delete config[key];
  });

  return config;
}

/**
 * Hace que un clic en el campo abra el picker, reenviándolo al botón toggle.
 */
function enableClickToOpen(wrapper, toggleSelector) {
  if (wrapper.hasAttribute('data-mdb-no-click-open')) return;
  if (wrapper.dataset.companyClickOpen) return;

  const input = wrapper.querySelector('input');
  const toggle = wrapper.querySelector(toggleSelector);
  if (!input || !toggle) return;

  // Ni `readonly` ni `disabled`: el `open()` del Datepicker aborta con ambos
  // (`if (this._input.readOnly || this._input.disabled) return;`), así que
  // marcarlos dejaría el campo muerto. `inputmode="none"` evita el teclado en
  // móvil sin tocar el estado del campo ni sacarlo del submit.
  input.setAttribute('inputmode', 'none');
  input.style.cursor = 'pointer';

  input.addEventListener('click', () => {
    if (input.disabled) return;
    toggle.click();
  });

  keepLabelUpWhileOpen(wrapper, input);

  wrapper.dataset.companyClickOpen = 'true';
}

/**
 * Mantiene el label arriba mientras el panel está abierto.
 *
 * El label flota con la clase `active`, que MDB pone al enfocar el campo. Pero
 * aquí el panel se abre por el botón y **el input nunca recibe el foco**, así
 * que el label se queda abajo con el calendario desplegado: cae encima del
 * placeholder —«dd/mm/aaaa»— y se leen los dos textos cruzados.
 *
 * Al cerrar se decide por el contenido: si quedó una fecha, el label sigue
 * arriba; si no, vuelve a su sitio. No basta con dejárselo a MDB, porque su
 * manejador de `blur` tampoco llega a dispararse si nunca hubo foco.
 */
function keepLabelUpWhileOpen(wrapper, input) {
  const sube = () => input.classList.add('active');
  const decide = () => {
    if (!input.value) input.classList.remove('active');
  };

  // Los tres pickers emiten los mismos nombres bajo su propio espacio.
  ['datepicker', 'timepicker', 'datetimepicker'].forEach((nombre) => {
    wrapper.addEventListener(`open.mdb.${nombre}`, sube);
    wrapper.addEventListener(`close.mdb.${nombre}`, decide);
  });
}

/**
 * Instancia en español los pickers de `root` y les activa la apertura por clic.
 *
 * @param {ParentNode} [root=document] Ámbito a recorrer.
 * @param {Object} components Componentes de MDB ({ Datepicker, Timepicker, Datetimepicker }).
 */
export function setupPickers(root = document, components = {}) {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  PICKERS.forEach(({ selector, component, toggle, options }) => {
    const Component = components[component];
    if (!Component) return;

    root.querySelectorAll(selector).forEach((wrapper) => {
      // Hay que instanciar siempre, incluso con `data-mdb-no-locale`: el botón
      // toggle lo crea el componente al inicializarse, y sin él no hay nada a
      // lo que reenviar el clic. Sin idioma, `new Component(wrapper)` es
      // exactamente lo que haría el autoinit de MDB.
      if (!Component.getInstance(wrapper)) {
        const config = wrapper.hasAttribute('data-mdb-no-locale')
          ? undefined
          : withElementOverrides(wrapper, options());

        // eslint-disable-next-line new-cap
        new Component(wrapper, config);
      }

      enableClickToOpen(wrapper, toggle);
    });
  });
}

export default setupPickers;
